// 시청 화면 컨트롤 바 아이콘 10종 — 24×24 그리드 · stroke 2 · currentColor
// (`docs/컨트롤바-아이콘-착수지시서.md` §8). 색은 부모의 CSS `color` 를 따르고 크기는 CSS 가 정한다.
//
// ★ 「10」은 글꼴이 아니라 선(path)이다 — 기기 글꼴에 따라 깨지지 않는다(D5).
// ★ `focusable="false"` — 빠뜨리면 일부 TV 웹뷰에서 SVG 가 포커스 링에 끼어든다(규칙 29 의 후보는
//   `button`·`a[href]` 뿐이어야 한다).
import type { ReactElement } from 'react';

const STROKE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;

const SOLID = {
  fill: 'currentColor',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinejoin: 'round',
} as const;

const DIGITS = { ...STROKE, strokeWidth: 1.6 } as const;

const PATHS = {
  // 채널 번호·이름
  channel: (
    <>
      <rect x="2.5" y="7" width="19" height="13" rx="2.5" {...STROKE} />
      <path d="M8 2.5l4 4 4-4" {...STROKE} />
    </>
  ),
  // 홈
  home: (
    <>
      <path d="M3 10.5 12 3l9 7.5" {...STROKE} />
      <path d="M5.5 9v11a1 1 0 0 0 1 1H10v-6h4v6h3.5a1 1 0 0 0 1-1V9" {...STROKE} />
    </>
  ),
  // 채널 Up
  chUp: <path d="M5.5 15.5 12 9l6.5 6.5" {...STROKE} />,
  // 채널 Down
  chDown: <path d="M5.5 8.5 12 15l6.5-6.5" {...STROKE} />,
  // 전체 회차 · 전체 상품
  episodes: (
    <>
      <rect x="3" y="4" width="4.5" height="4" rx="1" {...STROKE} />
      <rect x="3" y="10" width="4.5" height="4" rx="1" {...STROKE} />
      <rect x="3" y="16" width="4.5" height="4" rx="1" {...STROKE} />
      <path d="M11 6h10M11 12h10M11 18h10" {...STROKE} />
    </>
  ),
  // 다음 회차 · 건너뛰기
  nextEpisode: (
    <>
      <path d="M5 5.5v13l10-6.5z" {...SOLID} />
      <path d="M19 5v14" {...STROKE} />
    </>
  ),
  // 10초 전
  back10: (
    <>
      <path d="M6.34 7.34A8 8 0 1 0 12 5" {...STROKE} />
      <path d="M14.8 2.3 12 5l2.8 2.7" {...STROKE} />
      <path d="M8.9 11.2 10.3 10.2V15.8" {...DIGITS} />
      <rect x="12.3" y="10.2" width="3.3" height="5.6" rx="1.65" {...DIGITS} />
    </>
  ),
  // 일시정지
  pause: (
    <>
      <rect x="6.5" y="5" width="3.5" height="14" rx="1" {...SOLID} />
      <rect x="14" y="5" width="3.5" height="14" rx="1" {...SOLID} />
    </>
  ),
  // 재생
  play: <path d="M8 5v14l11-7z" {...SOLID} />,
  // 10초 후
  fwd10: (
    <>
      <path d="M17.66 7.34A8 8 0 1 1 12 5" {...STROKE} />
      <path d="M9.2 2.3 12 5 9.2 7.7" {...STROKE} />
      <path d="M8.9 11.2 10.3 10.2V15.8" {...DIGITS} />
      <rect x="12.3" y="10.2" width="3.3" height="5.6" rx="1.65" {...DIGITS} />
    </>
  ),
} satisfies Record<string, ReactElement>;

export type PlayerIconName = keyof typeof PATHS;

/**
 * 아이콘 하나. **이름을 말하지 않는다**(`aria-hidden`) — 이름은 그 버튼의 `aria-label` 이 갖고,
 * 그 문구는 사전 키를 거친다(절대 규칙 24).
 */
export function PlayerIcon({ name }: { name: PlayerIconName }) {
  return (
    <svg
      className="player-icon"
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      data-icon={name}
    >
      {PATHS[name]}
    </svg>
  );
}
